"use client";

import { useEffect, useRef, useState, RefObject, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { useOnClickOutside } from "usehooks-ts";

type DropdownProps = {
  buttonRef: RefObject<HTMLElement>;
  children: React.ReactNode;
  className?: string;
  sizeType?: "dynamic" | "parent";
  direction?: "vertical" | "horizontal";
  triggerType?: "click" | "hover"; // <-- nový prop
};

const Dropdown = ({
                    buttonRef,
                    children,
                    className,
                    sizeType = "dynamic",
                    direction = "vertical",
                    triggerType = "click", // <-- výchozí chování
                  }: DropdownProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownSize, setDropdownSize] = useState({ width: 0, height: 0 });
  const [parentWidth, setParentWidth] = useState(0);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: "bottom-right",
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useOnClickOutside(dropdownRef, (e) => {
    if (buttonRef.current?.contains(e.target as Node)) return;
    setIsOpen(false);
  });

  // ⬇️ Kliknutí
  useEffect(() => {
    if (triggerType !== "click") return;

    const handleClick = (e: MouseEvent) => {
      e.stopPropagation();
      setIsOpen((prev) => !prev);
    };

    const btn = buttonRef.current;
    if (btn) btn.addEventListener("click", handleClick);
    return () => {
      if (btn) btn.removeEventListener("click", handleClick);
    };
  }, [buttonRef, triggerType]);

  // ⬇️ Hover
  useEffect(() => {
    if (triggerType !== "hover") return;

    const btn = buttonRef.current;
    const dropdown = dropdownRef.current;

    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);

    btn?.addEventListener("mouseenter", open);
    btn?.addEventListener("mouseleave", close);

    dropdown?.addEventListener("mouseenter", open);
    dropdown?.addEventListener("mouseleave", close);

    return () => {
      btn?.removeEventListener("mouseenter", open);
      btn?.removeEventListener("mouseleave", close);
      dropdown?.removeEventListener("mouseenter", open);
      dropdown?.removeEventListener("mouseleave", close);
    };
  }, [buttonRef, triggerType, dropdownRef]);

  const updatePosition = useCallback(() => {
    if (!isOpen || !buttonRef.current || !dropdownRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = dropdownSize.width;
    const dropdownHeight = dropdownSize.height;
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;

    const spaceBelow = screenH - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const spaceRight = screenW - buttonRect.right;
    const spaceLeft = buttonRect.left;

    let top = 0;
    let left = 0;
    let placement = "";

    const fallbackVertical = () => {
      if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
        top = buttonRect.bottom + window.scrollY + 4;
        placement = "bottom-right";
      } else {
        top = buttonRect.top + window.scrollY - dropdownHeight - 4;
        placement = "top-right";
      }

      if (spaceRight < dropdownWidth && spaceLeft > dropdownWidth) {
        left = buttonRect.left + buttonRect.width - dropdownWidth + window.scrollX;
        placement = placement.includes("top") ? "top-left" : "bottom-left";
      } else {
        left = buttonRect.left + window.scrollX;
      }
    };

    if (direction === "vertical") {
      fallbackVertical();
    } else {
      if (spaceRight >= dropdownWidth) {
        top = buttonRect.top + window.scrollY;
        left = buttonRect.right + window.scrollX + 4;
        placement = "right";
      } else if (spaceLeft >= dropdownWidth) {
        top = buttonRect.top + window.scrollY;
        left = buttonRect.left + window.scrollX - dropdownWidth - 4;
        placement = "left";
      } else {
        fallbackVertical();
      }
    }

    if (sizeType === "parent") {
      setParentWidth(buttonRect.width);
    }

    setPosition({ top, left, placement });
  }, [isOpen, buttonRef, dropdownSize, sizeType, direction]);

  useEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, dropdownSize, buttonRef, sizeType, direction, updatePosition]);

  useEffect(() => {
    if (!dropdownRef.current) return;

    const observer = new ResizeObserver(() => {
      if (dropdownRef.current) {
        setDropdownSize({
          width: dropdownRef.current.offsetWidth,
          height: dropdownRef.current.offsetHeight,
        });
      }
    });

    observer.observe(dropdownRef.current);
    return () => observer.disconnect();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, dropdownSize, direction, updatePosition]);

  if (!isMounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{
            opacity: 0,
            y:
              direction === "vertical" ||
              position.placement.includes("top") ||
              position.placement.includes("bottom")
                ? position.placement.includes("top")
                  ? 10
                  : -10
                : 0,
            x:
              direction === "horizontal" ||
              position.placement === "left" ||
              position.placement === "right"
                ? position.placement === "left"
                  ? 10
                  : -10
                : 0,
          }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{
            opacity: 0,
            y:
              direction === "vertical" ||
              position.placement.includes("top") ||
              position.placement.includes("bottom")
                ? position.placement.includes("top")
                  ? 10
                  : -10
                : 0,
            x:
              direction === "horizontal" ||
              position.placement === "left" ||
              position.placement === "right"
                ? position.placement === "left"
                  ? 10
                  : -10
                : 0,
          }}
          transition={{ duration: 0.2 }}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            position: "absolute",
            minWidth: sizeType === "dynamic" ? "max-content" : `${parentWidth}px`,
          }}
          className={clsx(
            "rounded-xl border border-borderGray bg-sidebar shadow-md z-50",
            className,
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Dropdown;
